import React, {FC, useMemo} from "react";
import PhoneNumberInput, {isValidPhoneNumber} from "react-phone-number-input";
import {isSupportedCountry, parsePhoneNumberFromString} from "libphonenumber-js";
import type {CountryCode} from "libphonenumber-js";
import ruLabels from "react-phone-number-input/locale/ru.json";
import uaLabels from "react-phone-number-input/locale/ua.json";
import "react-phone-number-input/style.css";
import styles from "./PhoneInput.module.scss";

export type PhoneInputProps = {
    value?: string;
    onChange: (value?: string) => void;
    onCountryChange?: (country?: string) => void;
    error?: boolean;
    language?: "Ru" | "Ua";
    id?: string;
    placeholder?: string;
    disabled?: boolean;
    readOnly?: boolean;
    className?: string;
};

const STORAGE_KEY = "avtomir.phone.defaultCountry";

const getDefaultCountry = (): CountryCode => {
    if (typeof window === "undefined") return "UA";
    const stored = window.localStorage.getItem(STORAGE_KEY) as CountryCode;
    return stored && isSupportedCountry(stored) ? stored : "UA";
};

const PhoneInput: FC<PhoneInputProps> = ({value, onChange, onCountryChange, error, language = "Ru", readOnly = false, className, ...props}) => {
    const defaultCountry = getDefaultCountry();
    const normalizedValue = useMemo(() => {
        if (!value) return value;
        return parsePhoneNumberFromString(value, defaultCountry)?.number
            || parsePhoneNumberFromString(value, "UA")?.number
            || value;
    }, [defaultCountry, value]);

    const handleCountryChange = (country?: string) => {
        if (country && typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, country);
        onCountryChange?.(country);
    };

    return <PhoneNumberInput
        {...props}
        international
        defaultCountry={defaultCountry}
        value={normalizedValue}
        onChange={onChange}
        onCountryChange={handleCountryChange}
        labels={language === "Ua" ? uaLabels : ruLabels}
        className={[className, error ? styles.error : "", readOnly ? styles.readOnly : ""].filter(Boolean).join(" ") || undefined}
        countrySelectProps={readOnly ? {disabled: true, tabIndex: -1} : undefined}
        numberInputProps={{"aria-invalid": error, readOnly, tabIndex: readOnly ? -1 : undefined}}
    />;
};

export {isValidPhoneNumber};
export default PhoneInput;
